import {
  AllocationStatus,
  RegistrationMode,
} from "@timeedit/registration-schemas";
import {
  CheckLinkedGroupsResult,
  ENABLE_STUDENT_TO_DEALLCOATE,
  exhaustiveMatchingGuard,
  replaceBraces,
} from "@timeedit/registration-shared";
import { ButtonProps, Tooltip, Typography } from "@timeedit/ui-components";
import CheckCircleOutlined from "@timeedit/ui-components/icons/CheckCircleOutlined";
import CheckOutlined from "@timeedit/ui-components/icons/CheckOutlined";
import DeleteOutlined from "@timeedit/ui-components/icons/DeleteOutlined";
import Queue from "@timeedit/ui-components/icons/Queue";
import SwapOutlined from "@timeedit/ui-components/icons/SwapOutlined";
import WarningOutlined from "@timeedit/ui-components/icons/WarningOutlined";
import cn from "classnames";
import { ComponentProps, ReactNode, useId, useState } from "react";
import "./allocationControls.scss";
import {
  DescriptionDisabled,
  Translations,
  WaitlistButtonProps,
} from "./types";
import { useDisabledWaitlistState } from "./hooks";
import {
  AccessibleLabel,
  ButtonWithDescription,
} from "./ButtonWithDescription";
import { ConflictNotification } from "../Conflict";
import { StatusModal } from "../StatusModal";

export interface AllocationControlsProps
  extends Omit<ButtonProps, "disabled" | "aria-labelledby"> {
  groupId: number;
  /** Ids of the elements naming this control, e.g. from `groupControlsLabelledBy(groups)`. */
  labelledBy?: string;
  currentLoadingGroup: number;
  translations: Translations;
  onAllocate: () => void;
  onDeallocate: () => void;
  full?: boolean;
  clash?: boolean;
  blockStudentConflict?: boolean;
  allocationStatus?: AllocationStatus;
  mode?: RegistrationMode;
  open?: boolean;
  linkedValidation?: CheckLinkedGroupsResult;
  disabledState?: DescriptionDisabled;
  groupName: string;
  activityName: string;
  waitlistProps: {
    onJoinWaitlist: () => void;
    onLeaveWaitlist: () => void;
    isOnWaitlist: boolean;
    enableWaitlist: boolean;
    hasStudentsInWaitlist: boolean;
  };
}

/**
 *
 * @param translations - Translations for various button texts and messages
 * @param onAllocate - Callback when the register button is clicked
 * @param onDeallocate - Callback when the deallocate button is clicked
 * @param full - Whether the group is full
 * @param clash - Whether there is a scheduling clash with the group
 * @param blockStudentConflict - If we should allow a student to allocate through conflict
 * @param allocationStatus - Current allocation status of the user for this group
 * @param mode - Registration mode, who is using the controls
 * @param open - Whether the group is open for registration
 * @param groupId - ID of the group being allocated to
 * @param labelledBy - Ids of the elements naming this control (group tags and activity header)
 * @param currentLoadingGroup - ID of the group currently loading (for showing loading states)
 * @param linkedValidation - Result of linked group validation
 * @param disabledState - Object describing if the button should be disabled and why (does not override logic if disabled state is set to false)
 * @param activityName - Name of the activity related to the current button
 * @param groupName - Name of the group related to the current button
 * @param waitlistProps.isOnWaitlist - Whether the user is currently on the waitlist in this group
 * @param waitlistProps.enableWaitlist - Whether waitlist feature is enabled
 * @param waitlistProps.onJoinWaitlist - Callback when the join waitlist button is clicked
 * @param waitlistProps.onLeaveWaitlist - Callback when the leave waitlist button is clicked
 * @param waitlistProps.hasStudentsInWaitlist - Whether there are any students in the waitlist for this group
 *
 */
export function AllocationControls({
  translations,
  onAllocate,
  onDeallocate,
  full = false,
  clash = false,
  blockStudentConflict = false,
  allocationStatus = "NOT_ALLOCATED",
  mode = "student",
  open = false,
  groupId,
  labelledBy,
  currentLoadingGroup,
  linkedValidation = { success: true, message: "" },
  disabledState,
  waitlistProps,
  activityName,
  groupName,
  ...buttonProps
}: AllocationControlsProps): React.JSX.Element {
  const {
    isOnWaitlist = false,
    enableWaitlist = false,
    onJoinWaitlist,
    onLeaveWaitlist,
    hasStudentsInWaitlist,
  } = waitlistProps;
  const { waitlistState, disabled, description } = useDisabledWaitlistState({
    enableWaitlist,
    full,
    allocationStatus,
    mode,
    open,
    blockStudentConflict,
    clash,
    linkedValidationSuccess: linkedValidation.success,
    isOnWaitlist,
    disabledState,
    translations,
    hasStudentsInWaitlist,
  });

  const loadingThis =
    currentLoadingGroup > 0 && currentLoadingGroup === groupId;
  const loadingOther =
    currentLoadingGroup > 0 && currentLoadingGroup !== groupId;
  const isLoading = loadingThis || Boolean(buttonProps.loading);

  return (
    <Tooltip title={mode === "teacher" ? linkedValidation.message : ""}>
      <div className={cn("allocation-container", buttonProps.className)}>
        {renderContent()}
        <div role="status" className="allocation-container__status">
          {getStatusText()}
        </div>
      </div>
    </Tooltip>
  );

  function getStatusText(): string | undefined {
    if (allocationStatus === "ALLOCATED_TO_THIS") {
      return translations.registered;
    }
    if (isOnWaitlist) {
      return translations.inWaitlist;
    }
    if (waitlistState.shouldShowWaitlist) {
      return translations.notInWaitlist;
    }
    return undefined;
  }

  function renderContent(): React.JSX.Element {
    return (
      <AllocationButton
        {...buttonProps}
        mode={mode}
        linkedIssue={mode === "teacher" && !linkedValidation.success}
        loading={isLoading}
        size="large"
        translations={translations}
        clash={clash}
        allocationStatus={allocationStatus}
        disabled={disabled || loadingOther}
        description={description}
        onAllocate={onAllocate}
        onDeallocate={onDeallocate}
        groupName={groupName}
        activityName={activityName}
        labelledBy={labelledBy}
        waitListProps={{
          waitlistState,
          isOnWaitlist,
          onJoinWaitlist,
          onLeaveWaitlist,
        }}
      />
    );
  }
}

interface AllocationButtonProps extends Omit<ButtonProps, "aria-labelledby"> {
  translations: Translations;
  labelledBy?: string;
  onAllocate: () => void;
  onDeallocate: () => void;
  clash: boolean;
  allocationStatus?: AllocationStatus;
  description: ReactNode;
  linkedIssue: boolean;
  groupName: string;
  activityName: string;
  waitListProps: WaitlistButtonProps;
  mode?: RegistrationMode;
}

function AllocationButton({
  translations,
  onAllocate,
  onDeallocate,
  clash = false,
  waitListProps,
  description,
  linkedIssue = false,
  allocationStatus = "NOT_ALLOCATED",
  groupName,
  activityName,
  labelledBy,
  mode,
  ...buttonProps
}: AllocationButtonProps): React.JSX.Element {
  const { isOnWaitlist, onJoinWaitlist, onLeaveWaitlist, waitlistState } =
    waitListProps;
  const conflictId = useId();
  const linkedIssueId = useId();
  const [confirm, setConfirm] = useState<"deallocate" | "leaveWaitlist">();
  const allowDeallocate = ENABLE_STUDENT_TO_DEALLCOATE || mode === "teacher";
  const { label, hint, ...stateButtonProps } = getButtonProps();

  return (
    <>
      <StatusModal
        severity="Warning"
        open={confirm === "deallocate"}
        title={translations.deallocateConfirm}
        okText={translations.deallocate}
        closeIcon={false}
        onOk={() => {
          onDeallocate();
          setConfirm(undefined);
        }}
        onCancel={() => setConfirm(undefined)}
      />
      <StatusModal
        open={confirm === "leaveWaitlist"}
        title={translations.leaveWaitlistConfirmTitle}
        severity="Warning"
        okText={translations.leaveWaitlist}
        closeIcon={false}
        onOk={() => {
          onLeaveWaitlist();
          setConfirm(undefined);
        }}
        onCancel={() => setConfirm(undefined)}
        text={
          <Typography.Text>
            {replaceBraces(
              translations.leaveWaitlistConfirmText,
              groupName,
              activityName
            )}
          </Typography.Text>
        }
      />
      <ButtonWithDescription
        {...buttonProps}
        notificationIds={[clash && conflictId, linkedIssue && linkedIssueId]}
        description={description}
        {...stateButtonProps}
        accessibleLabel={labelledBy ? { label, hint, labelledBy } : undefined}
      />
      {clash && <ConflictNotification useText id={conflictId} />}
      {linkedIssue && (
        <WarningOutlined
          id={linkedIssueId}
          className={"allocation-container--icon"}
          alt={translations.linkedIssuePresent}
        />
      )}
    </>
  );

  function getButtonProps(): StateButtonProps {
    switch (allocationStatus) {
      case "ALLOCATED_TO_THIS": {
        // Kept as a disabled button so focus stays in place after registering.
        if (!allowDeallocate) {
          return {
            disabled: true,
            className: cn(buttonProps.className, "registered-button"),
            icon: <CheckCircleOutlined aria-hidden />,
            label: translations.youAreRegisteredTo,
            children: translations.registered,
          };
        }
        return {
          type: "primary",
          icon: <CheckOutlined aria-hidden />,
          onClick: () => setConfirm("deallocate"),
          label: translations.registered,
          hint: translations.clickToDeregister,
          children: translations.registered,
        };
      }
      case "ALLOCATED_TO_OTHER": {
        return (
          getWaitlistButtonProps() ?? {
            icon: <SwapOutlined aria-hidden />,
            onClick: onAllocate,
            label: translations.swapTo,
            children: translations.swap,
          }
        );
      }
      case "NOT_ALLOCATED": {
        return (
          getWaitlistButtonProps() ?? {
            onClick: onAllocate,
            label: translations.registerTo,
            children: translations.register,
          }
        );
      }
      case "MULTIPLE_ALLOCATIONS": {
        return {
          danger: true,
          description: translations.multipleAllocations,
          onClick: onDeallocate,
          icon: <DeleteOutlined aria-hidden />,
          label: translations.multipleAllocations,
          hint: translations.clickToDeregister,
        };
      }
      default: {
        return exhaustiveMatchingGuard(allocationStatus);
      }
    }
  }

  function getWaitlistButtonProps(): StateButtonProps | undefined {
    if (isOnWaitlist) {
      return {
        isWaitlist: true,
        icon: <Queue aria-hidden />,
        onClick: () => setConfirm("leaveWaitlist"),
        label: translations.inWaitlist,
        hint: translations.clickToLeaveWaitlist,
        children: translations.inWaitlist,
      };
    }
    if (waitlistState.shouldShowWaitlist) {
      return {
        onClick: onJoinWaitlist,
        label: translations.waitlist,
        children: translations.waitlist,
      };
    }
    return undefined;
  }
}

type StateButtonProps = Pick<
  ComponentProps<typeof ButtonWithDescription>,
  | "type"
  | "disabled"
  | "className"
  | "danger"
  | "icon"
  | "isWaitlist"
  | "description"
  | "onClick"
  | "children"
> &
  Pick<AccessibleLabel, "label" | "hint">;
